import axios from 'axios';
import { once, keys } from 'lodash';
import Cookies from 'js-cookie';

const AUTH_COOKIE_NAME = process.env.REACT_APP_COOKIE_OPTIONS_AUTH_COOKIE_NAME;


function getCookieOptions() {
  const cookieOptions = {
    secure: process.env.REACT_APP_COOKIE_OPTIONS_SECURE,
    httpOnly: process.env.REACT_APP_COOKIE_OPTIONS_HTTP_ONLY, // https://latesthackingnews.com/2017/07/03/what-is-httponly-cookie/
    domain: process.env.REACT_APP_COOKIE_OPTIONS_DOMAIN,
    path: process.env.REACT_APP_COOKIE_OPTIONS_PATH,
    sameSite: process.env.REACT_APP_COOKIE_OPTIONS_SAME_SITE,
  };
  return cookieOptions;
}

export const API_URL = (typeof window === 'undefined' || process.env.NODE_ENV === 'test') ?
  (`${process.env.REACT_APP_BASE_URL}/api`) :
  '/api';

export function getCookie(cookieName = AUTH_COOKIE_NAME) {
  return Cookies.get(cookieName, getCookieOptions());
}

export function removeCookie(cookieName = AUTH_COOKIE_NAME) {
  Cookies.remove(cookieName, getCookieOptions());
}

export function setCookie(value, cookieName = AUTH_COOKIE_NAME) {
  if (value) {
    const expires = new Date();
    expires.setTime(expires.getTime() + (365 * 24 * 60 * 60 * 1000)); // a year
    Cookies.set(cookieName, value, { ...getCookieOptions(), expires });
    if (cookieName === AUTH_COOKIE_NAME) {
      axios.defaults.headers.common = {
        Authorization: `Bearer ${value}`,
      };
    }
  } else {
    removeCookie(cookieName);
    if (cookieName === AUTH_COOKIE_NAME) {
      axios.defaults.headers.common = {
        Authorization: null,
      };
    }
  }
}

function addConfig(config) {
  const commonHeader = { ...(axios.defaults.headers && axios.defaults.headers.common ? axios.defaults.headers.common : {}) };
  if (commonHeader.Authorization) {
    commonHeader['X-Authorization'] = commonHeader.Authorization; // safari override Authorization with basic auth
  }
  config.headers = {
    ...config.headers,
    ...commonHeader,
  };
  return config;
}

function isJsonString(str) {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
}

function responseInterceptorError(reason) {
  if (reason.response) {
    // The request was made, but the server responded with a status code
    // that falls out of the range of 2xx
    if (reason.config && reason.response && reason.response.status === 429) {
      // too many requests
      window.location.href = '/errors/too-many-requests';
    }

    // is reason.response?.data a json
    if (isJsonString(reason.response?.data)) {
      return Promise.reject(reason.response);
    }

    return Promise.reject({
      message: reason.response?.data?.message || reason.message,
      name: reason.name,
      code: reason.code,
      data: reason.response?.data,
    });

  }
  // Something happened in setting up the request that triggered an Error
  return Promise.reject(reason.message);
}
function responseInterceptorSuccess(response) {
  return response.data;
}

const getInstance = once(() => {
  const params = {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    baseURL: `${API_URL}`,
    responseType: 'json',
  };
  const instance = axios.create(params);
  instance.interceptors.request.use(addConfig);
  instance.interceptors.response.use(
    responseInterceptorSuccess,
    responseInterceptorError,
  );
  return instance;
});

export function jsonToQueryString(json) {
  const rest = keys(json).map(key => `${encodeURIComponent(key)}=${encodeURIComponent(json[key])}`).join('&');
  return `?${rest}`;
}

export default function callApi(endpoint, method = 'get', data, withToken = true, config = {}) {
  const urlParams = method.toLowerCase() === 'get' ? jsonToQueryString(data) : '';
  return getInstance().request({
    method,
    url: `/${endpoint}${urlParams}`,
    data,
    withToken,
    ...config,
  });
}
